/**
 * RecommendationCard Component
 *
 * Displays a full recommendation with specs, impact details, and handoff context.
 * Uses CollapsibleSection for the expandable details.
 */

import { useState } from "react";
import type { UnifiedRecommendation, UnifiedPriorityLevel } from "@a7y/types";
import { EffortWorkTypeBadges } from "./EffortWorkTypeBadges";
import { ImpactBadge } from "./ImpactBadge";
import { DevCardBadge } from "./DevCardBadge";
import { CollapsibleSection } from "./CollapsibleSection";

interface RecommendationCardProps {
  recommendation: UnifiedRecommendation;
  className?: string;
}

const priorityColors: Record<UnifiedPriorityLevel, string> = {
  "Priority 1": "border-l-red-500",
  "Priority 2": "border-l-yellow-500",
  "Priority 3": "border-l-blue-500",
};

const priorityBadgeColors: Record<UnifiedPriorityLevel, string> = {
  "Priority 1": "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "Priority 2": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  "Priority 3": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
};

export function RecommendationCard({ recommendation, className = "" }: RecommendationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [exampleExpanded, setExampleExpanded] = useState(false);
  const hasDetails = recommendation.specs || recommendation.handoff_context || recommendation.constraints;
  const isStrategicInsight = recommendation.work_type === "Strategic-Insight";

  const borderColor = isStrategicInsight ? "border-l-zinc-400" : priorityColors[recommendation.priority];

  return (
    <DevCardBadge name="RecommendationCard">
    <div className={`border border-gray-200 dark:border-zinc-700 border-l-4 ${borderColor} rounded-lg overflow-hidden ${isStrategicInsight ? "bg-zinc-50 dark:bg-zinc-900/30" : ""} ${className}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${priorityBadgeColors[recommendation.priority]}`}>
                {recommendation.priority}
              </span>
              {recommendation.impact_tier && !isStrategicInsight && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  recommendation.impact_tier === "High"
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                    : recommendation.impact_tier === "Medium"
                      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                      : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                }`}>
                  {recommendation.impact_tier} Impact
                </span>
              )}
              <EffortWorkTypeBadges effort={recommendation.effort} workType={recommendation.work_type} />
            </div>
            <p className="text-sm text-gray-900 dark:text-white">
              {recommendation.recommendation}
            </p>
          </div>
        </div>

        {/* Impact */}
        {!isStrategicInsight && (
          <div className="mt-3">
            <ImpactBadge summary={recommendation.impact.summary} details={recommendation.impact.details} />
          </div>
        )}

        {/* Why it matters */}
        {recommendation.why_it_matters && (
          <div className="mt-3 rounded-md bg-slate-50 dark:bg-slate-800/40 px-3 py-2">
            <h5 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Why this matters</h5>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{recommendation.why_it_matters}</p>
          </div>
        )}

        {/* Concrete example */}
        {recommendation.concrete_example && (
          <div className="mt-3">
            <CollapsibleSection
              title="Try this"
              isExpanded={exampleExpanded}
              onToggle={() => setExampleExpanded(!exampleExpanded)}
              headerVariant="inline"
            >
              <div className="rounded-md border-l-2 border-emerald-400 dark:border-emerald-600 bg-emerald-50/50 dark:bg-emerald-900/10 pl-3 pr-3 py-2">
                <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">{recommendation.concrete_example}</p>
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {hasDetails && (
        <div className="px-4 pb-4">
          <CollapsibleSection
            title="Details"
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
            headerVariant="inline"
          >
            <div className="pt-2 border-t border-gray-100 dark:border-zinc-700 space-y-4">
              {/* Specs */}
              {recommendation.specs && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Specs</h5>
                  <div className="bg-gray-50 dark:bg-zinc-800 rounded-md p-3 text-sm space-y-1">
                    {recommendation.specs.mode && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Mode:</span>
                        <span className="font-medium">{recommendation.specs.mode}</span>
                      </div>
                    )}
                    {recommendation.specs.confidence && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Confidence:</span>
                        <span className="font-medium">{recommendation.specs.confidence}</span>
                      </div>
                    )}
                    {recommendation.specs.hex_codes && recommendation.specs.hex_codes.length > 0 && (
                      <div className="flex gap-2 items-center">
                        <span className="text-gray-500 dark:text-gray-400">Colors:</span>
                        <div className="flex gap-1">
                          {recommendation.specs.hex_codes.map((hex, i) => (
                            <div
                              key={i}
                              className="w-5 h-5 rounded border border-gray-300 dark:border-zinc-600"
                              style={{ backgroundColor: hex }}
                              title={hex}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {recommendation.specs.text_size_ratios && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Text Sizes:</span>
                        <span className="font-medium">{recommendation.specs.text_size_ratios}</span>
                      </div>
                    )}
                    {recommendation.specs.layout_zones && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Layout:</span>
                        <span className="font-medium">{recommendation.specs.layout_zones}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Handoff Context */}
              {recommendation.handoff_context && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Handoff Context</h5>
                  <div className="bg-gray-50 dark:bg-zinc-800 rounded-md p-3 text-sm space-y-1">
                    {recommendation.handoff_context.severity !== undefined && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Severity:</span>
                        <span className="font-medium">{recommendation.handoff_context.severity}/100</span>
                      </div>
                    )}
                    {recommendation.handoff_context.affected_percentage !== undefined && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Affected Users:</span>
                        <span className="font-medium">{recommendation.handoff_context.affected_percentage}%</span>
                      </div>
                    )}
                    {recommendation.handoff_context.review_mentions && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Mentioned in:</span>
                        <span className="font-medium">
                          {recommendation.handoff_context.review_mentions.mentioned} of{' '}
                          {recommendation.handoff_context.review_mentions.sample} recent written reviews
                        </span>
                      </div>
                    )}
                    {recommendation.handoff_context.business_impact && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Business Impact:</span>
                        <span className="font-medium">{recommendation.handoff_context.business_impact}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Constraints */}
              {recommendation.constraints && recommendation.constraints.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Constraints</h5>
                  <ul className="text-sm space-y-1">
                    {recommendation.constraints.map((constraint, i) => (
                      <li key={i} className="text-zinc-700 dark:text-zinc-200">
                        <span className="text-gray-400">[{constraint.engine}]</span> {constraint.constraint}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Dependencies */}
              {recommendation.dependencies && recommendation.dependencies.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Dependencies</h5>
                  <div className="flex flex-wrap gap-1">
                    {recommendation.dependencies.map((dep, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-zinc-700 text-xs text-gray-600 dark:text-gray-400">
                        {dep}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>
        </div>
      )}
    </div>
    </DevCardBadge>
  );
}
